import { cache } from 'react'

/**
 * Static storage for feature flags to avoid Context overhead for read-only components.
 */
let clientFeatureFlags = {}
let clientFeaturePathOverrides = {}

/**
 * Server-side request-isolated store.
 * React's cache() ensures this object is unique per request.
 */
const getServerStore = cache(() => ({
  flags: {},
  overrides: {},
}))

/**
 * Initializes the feature flags. Should be called once at the top level (e.g., PageProviderClient).
 *
 * @param {Object} flags - The feature toggle object from server props.
 * @param {Object} [pathOverrides={}] - Route-specific overrides.
 */
export const initFeatureFlags = (flags, pathOverrides = {}) => {
  if (typeof window === 'undefined') {
    const store = getServerStore()
    store.flags = flags
    store.overrides = pathOverrides
  } else {
    // Merge server flags into existing client flags to preserve any manual updates
    clientFeatureFlags = { ...flags, ...clientFeatureFlags }
    clientFeaturePathOverrides = pathOverrides
  }
}

/**
 * Adds flags to the per-request server store without dropping flags already loaded.
 * Use when a caller only knows a subset of flags (e.g. the root layout's pilot flag) and must not
 * replace a full toggle object set by `getFeatureToggleObject`. No-op on the client, where
 * `initFeatureFlags` already merges.
 *
 * @param {Object} flags - Flags to add; incoming values win over existing ones.
 */
export const mergeFeatureFlags = (flags) => {
  if (typeof window !== 'undefined') return
  const store = getServerStore()
  store.flags = { ...store.flags, ...flags }
}

/**
 * Updates a specific feature flag on the client side.
 * Useful for client-side experiments like LaunchDarkly.
 *
 * @param {string} name - The name of the flag to update.
 * @param {*} value - The new value of the flag.
 */
export const updateFeatureFlag = (name, value) => {
  console.log('updated feature flag', name, value)
  if (typeof window !== 'undefined') {
    clientFeatureFlags[name] = value
    console.log('updateFeatureFlag: clientFeatureFlags', clientFeatureFlags)
  }
}

/**
 * Utility function to get a feature flag value.
 *
 * @param {string} flagName - The name of the flag to retrieve.
 * @returns {*} The value of the feature flag.
 */
export const getFeatureFlag = (flagName) => {
  const store =
    typeof window === 'undefined'
      ? getServerStore()
      : { flags: clientFeatureFlags, overrides: clientFeaturePathOverrides }

  if (Object.hasOwn(store.overrides, flagName)) {
    return store.overrides[flagName]
  }
  return store.flags[flagName]
}
